import { BoolStatus } from '@/constants/common.constant'
import { BreakDown, OrderType } from './data.d'
import type { ICancelReason } from '@/constants/order.constant'
import { merchantCancelReason, ItemStatus, DefaultCancelReason } from '@/constants/order.constant'
import type { OrderItem, StoreSettlement } from '@/services/transaction/data.d'
import { DiscountType } from '@/services/transaction/data.d'
import { t } from '@/utils/i18n'
import BigNumber from 'bignumber.js'
import { AppCountry } from '@/constants/app.constant'

export const getCancelReasonText = (cancelReason: ICancelReason) => {
  const reason = merchantCancelReason.find((reason) => reason.id === String(cancelReason))
  return reason ? t(reason.value.key) : t(DefaultCancelReason.key)
}

export const getFormattedPrice = ({
  cartItem: { quantity },
  status,
  unitPrice,
  unitListPrice,
  subtotal,
  merchantSubtotal,
}: OrderItem) => {
  const price = unitPrice
  const isListPriceValid =
    ![ItemStatus.ModifiedPrice, ItemStatus.ModifiedAll].includes(status) &&
    unitListPrice !== undefined &&
    unitListPrice !== unitPrice

  return {
    price,
    priceSubTotal: merchantSubtotal || subtotal,
    ...(isListPriceValid && {
      listPrice: unitListPrice,
      listPriceSubTotal: new BigNumber(unitListPrice).multipliedBy(quantity || 0).toString(),
    }),
  }
}

export const calcTransactionAmount = (
  order: OrderType,
  storeSettlement?: StoreSettlement,
): BreakDown => {
  const { items, amount, flag } = order
  const { amount: settlementAmount, promotionSubsidy } = storeSettlement || {}
  const { subtotal: orderValue, merchantSubtotal, promotion } = amount!
  const total = (items || []).reduce(
    (total, item) => {
      const {
        cartItem: { quantity },
        unitListPrice,
        unitPrice,
        discountType,
      } = item
      const itemListPriceSubTotal = new BigNumber(unitListPrice).multipliedBy(quantity || 0)
      const itemUnitPriceSubTotal = new BigNumber(unitPrice).multipliedBy(quantity || 0)
      total.foodOriginPrice = total.foodOriginPrice.plus(itemListPriceSubTotal)
      if (discountType === DiscountType.FlashSale) {
        total.flashSaleDiscounts = total.flashSaleDiscounts.plus(
          itemListPriceSubTotal.minus(itemUnitPriceSubTotal),
        )
      } else if (discountType === DiscountType.PriceSlash) {
        total.itemDiscounts = total.itemDiscounts.plus(
          itemListPriceSubTotal.minus(itemUnitPriceSubTotal),
        )
      }
      return total
    },
    {
      foodOriginPrice: new BigNumber(0),
      itemDiscounts: new BigNumber(0),
      flashSaleDiscounts: new BigNumber(0),
    },
  )

  const isCheapMeal = flag?.isCheapMeal === BoolStatus.True

  const totalAmount = new BigNumber(merchantSubtotal || orderValue)
    .plus(amount!.merchantSurchargeFee || 0)
    .minus(promotionSubsidy || 0)
    .toString()

  // ID opt out 了 prepaid sku combo 字段拆分，需要把combo的subsidy加到prepaid subsidy上面去，并清零拆分字段
  let merchantPrepaidSubsidyAmount = new BigNumber(settlementAmount?.merchantPrepaidSubsidy || '0')
  let prepaidSkuComboSubsidyAmount = new BigNumber(
    settlementAmount?.prepaidSkuComboMerchantSubsidy || '0',
  )
  if (AppCountry.isId) {
    merchantPrepaidSubsidyAmount = merchantPrepaidSubsidyAmount.plus(
      settlementAmount?.prepaidSkuComboMerchantSubsidy || '0',
    )
    prepaidSkuComboSubsidyAmount = prepaidSkuComboSubsidyAmount.minus(
      settlementAmount?.prepaidSkuComboMerchantSubsidy || '0',
    )
  }
  return {
    orderValue: merchantSubtotal || orderValue,
    foodOriginPrice: total.foodOriginPrice.toString(),
    flashSaleDiscountsAmount: total.flashSaleDiscounts.toString(),
    itemDiscountAmount: isCheapMeal
      ? promotion?.itemDiscountAmount || '0'
      : total.itemDiscounts.toString(),
    surchargeFee: amount!.merchantSurchargeFee || '0',
    totalAmount,
    promotionSubsidy: promotionSubsidy || '0',
    voucherMerchantSubsidyAmount: settlementAmount?.itemVoucherSubsidy || '0',
    voucherMerchantPwdSubsidyAmount: settlementAmount?.itemVoucherPwdSubsidy || '0',
    voucherShippingSubsidyAmount: settlementAmount?.shippingFeeVoucherSubsidy || '0',
    merchantPrepaidSubsidyAmount: merchantPrepaidSubsidyAmount.toString(),
    prepaidSkuComboSubsidyAmount: prepaidSkuComboSubsidyAmount.toString(),
    flashsalePlatformSubsidyAmount: settlementAmount?.flashsalePlatformSubsidy || '0',
    directDiscountAmount: promotion?.itemDiscountAmount || '0',
    merchantGroupDiscountSubsidyAmount: settlementAmount?.merchantGroupDiscountSubsidy || '0',
  }
}
